/**
 * Navigation Layout Admin
 *
 * Drag-and-drop editor for sidebar navigation layout per organization.
 * Allows admins to:
 *  - Move pages between sidebar sections
 *  - Reorder items within sections
 *  - Pin items to top of section
 *  - Override nav labels
 *  - Preview what the sidebar looks like for each role
 *  - Reset all overrides to defaults
 */

import { useTabbedChrome } from '@/components/weavein';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical, Pin, PinOff, Pencil, Check, X, RotateCcw, RotateCw,
  Save, Loader2, AlertCircle, ChevronDown, Search, Eye,
  Home, MessageSquare, Users, ArrowLeftRight, Calendar, FileText,
  MapPin, LayoutDashboard, BarChart3, Settings, User, Briefcase,
  MoreHorizontal, Lock, Phone, Plus, Trash2, UserCog,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { resolveNavIcon } from '@/components/v2/navIcons';
import { apiClient } from '@/services/apiClient';
import { useAppTranslation } from '@/contexts/TranslationContext';

// Per-org role loaded from /api/cbos/{id}/all-roles. `id` may be -1 for virtual
// system-role entries that don't yet have a role_terminology row — those can't
// scope nav-config rows, so we filter them out before showing the dropdown.
interface OrgRole {
  id: number;
  role_type: string;
  display_name_en: string;
  display_name_es?: string | null;
  system_role: string;
}

// ============================================================================
// TYPES
// ============================================================================

interface NavItemData {
  page_key: string;
  page_name: string;
  nav_label: string | null;
  icon: string | null;
  default_nav_section: string | null;
  nav_tab_id: string | null;
  default_display_order: number;
  category: string | null;
  parent_capability: string | null;
  default_enabled: boolean;
  // Overrides
  nav_section_override: string | null;
  display_order_override: number | null;
  nav_label_override: string | null;
  nav_label_override_es?: string | null;
  pinned: boolean;
  hidden: boolean;
  // Computed
  effective_section: string | null;
  effective_order: number;
  effective_label: string;
  // Org page status
  org_page_enabled: boolean | null;
  org_display_behavior: string | null;
  // Effective sidebar visibility — mirrors what GET /nav-config actually renders.
  // Optional so the editor still works against a backend that predates the field.
  effective_has_access?: boolean;
  effective_access_source?: string | null;
  hub_gated?: boolean;
  will_render?: boolean;
  // Why this page won't appear in the sidebar: 'hidden' | 'org_disabled' |
  // 'no_access' | 'feature_off' | 'hub_only' | 'unassigned' | null.
  render_block_reason?: string | null;
}

// Badge + styling for pages configured into a section that WON'T render in the
// real sidebar. 'hidden' is handled separately (it's an admin toggle, not a gate).
const BLOCK_BADGES: Record<string, { label: string; cls: string }> = {
  org_disabled: { label: 'disabled', cls: 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400' },
  no_access: { label: 'no access', cls: 'bg-rose-100 text-rose-700 dark:bg-rose-900/60 dark:text-rose-300' },
  feature_off: { label: 'feature off', cls: 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400' },
  hub_only: { label: 'hub only', cls: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-300' },
};

// Resolve why an item won't render, preferring the backend's effective decision
// and falling back to the legacy org_page_enabled flag for older backends.
function resolveBlockReason(item: NavItemData): string | null {
  if (item.will_render === false) return item.render_block_reason ?? 'org_disabled';
  if (item.will_render === undefined && item.org_page_enabled === false) return 'org_disabled';
  return null;
}

interface SectionData {
  section_key: string;
  section_name: string;
  section_name_es: string | null;
  icon: string | null;
  display_order: number;
  is_default: boolean;
  is_active: boolean;
}

interface Props {
  organizationId: number;
  organizationName?: string;
}

// ============================================================================
// ICON MAP
// ============================================================================

// One map for the sidebar and this editor (components/v2/navIcons.ts).
function IconForName({ name, size = 14 }: { name?: string | null; size?: number }) {
  const Icon = resolveNavIcon(name);
  return <Icon size={size} />;
}

// ============================================================================
// SORTABLE ITEM
// ============================================================================

function SortableNavItem({
  item,
  onTogglePin,
  onEditLabel,
  onToggleHidden,
  isOverride,
}: {
  item: NavItemData;
  onTogglePin: (pageKey: string) => void;
  onEditLabel: (pageKey: string, label: string) => void;
  onToggleHidden: (pageKey: string) => void;
  isOverride: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.page_key });

  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(item.effective_label);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const isHidden = item.hidden;
  const blockReason = resolveBlockReason(item);
  // Won't render for a reason other than the admin's hidden toggle (permission,
  // org-disable, feature-off, hub-only). These get grayed out with a reason badge.
  const isBlocked = !!blockReason && blockReason !== 'hidden';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
        isDragging ? 'z-50 shadow-lg' : ''
      } ${
        isHidden
          ? 'border-dashed border-amber-300 bg-amber-50/40 text-gray-500 dark:border-amber-700/60 dark:bg-amber-950/20 dark:text-gray-400'
          : isBlocked
            ? 'border-dashed border-gray-300 bg-gray-50 text-gray-400 dark:border-gray-600 dark:bg-gray-800/50 dark:text-gray-500'
            : isOverride
              ? 'border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/30'
              : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
      }`}
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab touch-none text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
      >
        <GripVertical size={14} />
      </button>

      {/* Icon */}
      <div className={`flex-shrink-0 ${isBlocked ? 'opacity-40' : 'text-gray-600 dark:text-gray-300'}`}>
        <IconForName name={item.icon} size={14} />
      </div>

      {/* Label */}
      {editing ? (
        <div className="flex flex-1 items-center gap-1">
          <input
            autoFocus
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onEditLabel(item.page_key, editValue);
                setEditing(false);
              }
              if (e.key === 'Escape') setEditing(false);
            }}
            className="flex-1 rounded border border-blue-300 bg-white px-1.5 py-0.5 text-xs dark:border-blue-600 dark:bg-gray-700 dark:text-gray-100"
          />
          <button onClick={() => { onEditLabel(item.page_key, editValue); setEditing(false); }} className="text-green-600"><Check size={12} /></button>
          <button onClick={() => setEditing(false)} className="text-gray-400"><X size={12} /></button>
        </div>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <span className={`truncate ${isBlocked ? '' : 'text-gray-800 dark:text-gray-100'}`}>
            {item.effective_label}
          </span>
          {isOverride && (
            <span className="rounded bg-blue-100 px-1 py-px text-[9px] font-semibold text-blue-700 dark:bg-blue-900 dark:text-blue-300">
              custom
            </span>
          )}
          {isBlocked && BLOCK_BADGES[blockReason!] && (
            <span
              title="Configured here, but won't appear in the sidebar for this role"
              className={`rounded px-1 py-px text-[9px] font-semibold ${BLOCK_BADGES[blockReason!].cls}`}
            >
              {BLOCK_BADGES[blockReason!].label}
            </span>
          )}
          {isHidden && (
            <span className="rounded bg-amber-100 px-1 py-px text-[9px] font-semibold text-amber-700 dark:bg-amber-900/60 dark:text-amber-300">
              hidden
            </span>
          )}
          {item.pinned && (
            <Pin size={10} className="text-amber-500" />
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
        <button
          onClick={() => setEditing(true)}
          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
          title="Edit label"
        >
          <Pencil size={12} />
        </button>
        <button
          onClick={() => onTogglePin(item.page_key)}
          className={`rounded p-1 ${
            item.pinned
              ? 'text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-950'
              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300'
          }`}
          title={item.pinned ? 'Unpin' : 'Pin to top'}
        >
          {item.pinned ? <PinOff size={12} /> : <Pin size={12} />}
        </button>
        <button
          onClick={() => onToggleHidden(item.page_key)}
          className={`rounded p-1 ${
            isHidden
              ? 'text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950'
              : 'text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950 dark:hover:text-red-400'
          }`}
          title={isHidden ? 'Restore to sidebar' : 'Remove from sidebar for this role'}
        >
          {isHidden ? <RotateCw size={12} /> : <Trash2 size={12} />}
        </button>
      </div>

      {/* Page key tooltip */}
      <span className="hidden text-[9px] text-gray-400 group-hover:inline dark:text-gray-500">
        {item.page_key}
      </span>
    </div>
  );
}

// ============================================================================
// DRAG OVERLAY ITEM
// ============================================================================

function DragOverlayItem({ item }: { item: NavItemData }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-blue-400 bg-white px-3 py-2 text-sm shadow-xl dark:border-blue-500 dark:bg-gray-800">
      <GripVertical size={14} className="text-blue-400" />
      <IconForName name={item.icon} size={14} />
      <span className="text-gray-800 dark:text-gray-100">{item.effective_label}</span>
    </div>
  );
}

// ============================================================================
// SECTION COLUMN
// ============================================================================

function SectionColumn({
  section,
  items,
  onTogglePin,
  onEditLabel,
  onToggleHidden,
}: {
  section: SectionData;
  items: NavItemData[];
  onTogglePin: (pk: string) => void;
  onEditLabel: (pk: string, label: string) => void;
  onToggleHidden: (pk: string) => void;
}) {
  const { setNodeRef } = useSortable({
    id: `section:${section.section_key}`,
    data: { type: 'section', sectionKey: section.section_key },
  });

  return (
    <div
      ref={setNodeRef}
      className="flex flex-col rounded-xl border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900"
    >
      {/* Section header */}
      <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <IconForName name={section.icon} size={16} />
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
          {section.section_name}
        </span>
        <span className="ml-auto rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-400">
          {items.length}
        </span>
      </div>

      {/* Items */}
      <div className="flex flex-col gap-1.5 p-3" style={{ minHeight: 60 }}>
        <SortableContext items={items.map(i => i.page_key)} strategy={verticalListSortingStrategy}>
          {items.length === 0 && (
            <div className="py-4 text-center text-xs text-gray-400 dark:text-gray-500">
              Drop items here
            </div>
          )}
          {items.map((item) => (
            <SortableNavItem
              key={item.page_key}
              item={item}
              onTogglePin={onTogglePin}
              onEditLabel={onEditLabel}
              onToggleHidden={onToggleHidden}
              isOverride={
                item.nav_section_override != null ||
                item.display_order_override != null ||
                item.nav_label_override != null ||
                item.hidden
              }
            />
          ))}
        </SortableContext>
      </div>
    </div>
  );
}

// ============================================================================
// UNASSIGNED POOL
// ============================================================================

function UnassignedPool({
  items,
  searchQuery,
  onSearchChange,
}: {
  items: NavItemData[];
  searchQuery: string;
  onSearchChange: (q: string) => void;
}) {
  const filtered = useMemo(() => {
    if (!searchQuery) return items;
    const q = searchQuery.toLowerCase();
    return items.filter(i =>
      i.effective_label.toLowerCase().includes(q) ||
      i.page_key.toLowerCase().includes(q) ||
      (i.category || '').toLowerCase().includes(q)
    );
  }, [items, searchQuery]);

  const { setNodeRef } = useSortable({
    id: 'section:_unassigned',
    data: { type: 'section', sectionKey: '_unassigned' },
  });

  return (
    <div
      ref={setNodeRef}
      className="rounded-xl border border-dashed border-gray-300 bg-gray-50/50 dark:border-gray-600 dark:bg-gray-900/50"
    >
      <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <span className="text-sm font-semibold text-gray-600 dark:text-gray-300">
          Available pages (not in sidebar)
        </span>
        <span className="ml-auto rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-400">
          {items.length}
        </span>
      </div>
      <div className="px-3 pt-2">
        <div className="relative">
          <Search size={12} className="absolute left-2.5 top-2 text-gray-400" />
          <input
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search pages…"
            className="w-full rounded-lg border border-gray-200 bg-white py-1.5 pl-7 pr-3 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
        </div>
      </div>
      <div className="flex flex-col gap-1 p-3" style={{ minHeight: 40, maxHeight: 300, overflowY: 'auto' }}>
        <SortableContext items={filtered.map(i => i.page_key)} strategy={verticalListSortingStrategy}>
          {filtered.length === 0 && (
            <div className="py-2 text-center text-xs text-gray-400">
              {searchQuery ? 'No matches' : 'All pages are assigned to sections'}
            </div>
          )}
          {filtered.map((item) => (
            <div
              key={item.page_key}
              className="flex items-center gap-2 rounded-lg border border-dashed border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
            >
              <IconForName name={item.icon} size={12} />
              <span className="flex-1 truncate">{item.effective_label}</span>
              <span className="text-[9px] text-gray-400">{item.category}</span>
            </div>
          ))}
        </SortableContext>
      </div>
    </div>
  );
}

// ============================================================================
// AVAILABLE ICONS for section picker
// ============================================================================

const AVAILABLE_ICONS: { name: string; icon: LucideIcon }[] = [
  { name: 'Home', icon: Home },
  { name: 'Briefcase', icon: Briefcase },
  { name: 'MessageSquare', icon: MessageSquare },
  { name: 'Users', icon: Users },
  { name: 'MapPin', icon: MapPin },
  { name: 'LayoutDashboard', icon: LayoutDashboard },
  { name: 'BarChart3', icon: BarChart3 },
  { name: 'Settings', icon: Settings },
  { name: 'Calendar', icon: Calendar },
  { name: 'FileText', icon: FileText },
  { name: 'ArrowLeftRight', icon: ArrowLeftRight },
  { name: 'MoreHorizontal', icon: MoreHorizontal },
  { name: 'Phone', icon: Phone },
  { name: 'User', icon: User },
  { name: 'Lock', icon: Lock },
];

// ============================================================================
// SECTION MANAGER
// ============================================================================

// ============================================================================
// SECTION TERMINOLOGY (per-org) — TRUS-528 Phase 2
// ============================================================================

interface SectionTermRow {
  section_key: string;
  default_label: string;
  default_label_es: string | null;
  default_order: number;
  icon: string | null;
  label_override: string | null;
  label_override_es: string | null;
  display_order_override: number | null;
  label: string;
  is_customized: boolean;
}

/**
 * Renames sidebar section headings for THIS organization.
 *
 * "Manage Sections" below edits nav_sections, which is a platform table shared
 * by every tenant — renaming a section there renames it for everyone, and it
 * is now platform-admin only for exactly that reason. This panel is what an
 * org admin wants instead: the same heading, reworded for one org.
 *
 * Org-scoped rather than per-role, unlike the page overrides in the grid
 * below. A role sees a different SET of pages; it does not call the same
 * section by a different name.
 */
function SectionTerminologyPanel({ organizationId }: { organizationId: number }) {
  const [isOpen, setIsOpen] = useState(false);
  const [rows, setRows] = useState<SectionTermRow[]>([]);
  const [draft, setDraft] = useState<Record<string, { en?: string; es?: string }>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(
        `/api/organizations/${organizationId}/nav-section-config`,
        { headers: { 'X-CBO-ID': organizationId.toString() } },
      );
      setRows(res.data?.sections || []);
      setDraft({});
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to load section names');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => { if (isOpen) load(); }, [isOpen, load]);

  const valueFor = (row: SectionTermRow, lang: 'en' | 'es') => {
    const d = draft[row.section_key];
    if (d && d[lang] !== undefined) return d[lang] as string;
    return (lang === 'en' ? row.label_override : row.label_override_es) ?? '';
  };

  const setValue = (key: string, lang: 'en' | 'es', value: string) =>
    setDraft((prev) => ({ ...prev, [key]: { ...prev[key], [lang]: value } }));

  const dirty = Object.keys(draft).length > 0;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const overrides = Object.entries(draft).map(([section_key, d]) => {
        const row = rows.find((r) => r.section_key === section_key);
        return {
          section_key,
          // An empty box means "use the platform name again", so send null
          // rather than "" — and carry the value we are NOT editing through
          // unchanged, since the API replaces the whole row.
          label_override: (d.en !== undefined ? d.en : row?.label_override ?? '') || null,
          label_override_es: (d.es !== undefined ? d.es : row?.label_override_es ?? '') || null,
          display_order_override: row?.display_order_override ?? null,
        };
      });
      await apiClient.put(
        `/api/organizations/${organizationId}/nav-section-config`,
        { overrides },
        { headers: { 'X-CBO-ID': organizationId.toString() } },
      );
      await load();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to save section names');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Return every sidebar section heading to its standard name?')) return;
    setSaving(true);
    setError(null);
    try {
      await apiClient.delete(
        `/api/organizations/${organizationId}/nav-section-config`,
        { headers: { 'X-CBO-ID': organizationId.toString() } },
      );
      await load();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to reset section names');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center gap-2 px-4 py-3 text-sm font-semibold text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700/50"
      >
        <Pencil size={14} />
        Section Names for This Organization
        <ChevronDown size={14} className={`ml-auto transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="border-t border-gray-200 px-4 pb-4 pt-3 dark:border-gray-700">
          <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
            Reword a sidebar heading for this organization only. Leave a box
            empty to keep the standard name. (&ldquo;Manage Sections&rdquo; below
            adds and removes sections for <em>every</em> organization and is
            restricted to platform administrators.)
          </p>

          {error && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center gap-2 py-3 text-xs text-gray-500">
              <Loader2 size={12} className="animate-spin" /> Loading…
            </div>
          ) : (
            <>
              <div className="mb-3 space-y-1.5">
                {rows.map((row) => (
                  <div
                    key={row.section_key}
                    className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-100 px-3 py-2 text-sm dark:border-gray-600"
                  >
                    <IconForName name={row.icon} size={14} />
                    <span className="w-40 shrink-0 text-xs text-gray-500 dark:text-gray-400">
                      {row.default_label}
                    </span>
                    <input
                      value={valueFor(row, 'en')}
                      placeholder={row.default_label}
                      disabled={saving}
                      onChange={(e) => setValue(row.section_key, 'en', e.target.value)}
                      className="min-w-[10rem] flex-1 rounded border border-gray-200 bg-white px-2 py-1.5 text-xs dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                    />
                    <input
                      value={valueFor(row, 'es')}
                      placeholder={row.default_label_es || 'Español'}
                      disabled={saving}
                      onChange={(e) => setValue(row.section_key, 'es', e.target.value)}
                      className="min-w-[10rem] flex-1 rounded border border-gray-200 bg-white px-2 py-1.5 text-xs dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                    />
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleSave}
                  disabled={saving || !dirty}
                  className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button
                  onClick={handleReset}
                  disabled={saving}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300"
                >
                  Reset to standard names
                </button>
                {dirty && <span className="text-[10px] text-gray-500">Unsaved changes</span>}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SectionManager({
  sections,
  onSectionCreated,
  onSectionUpdated,
  onSectionDeleted,
}: {
  sections: SectionData[];
  onSectionCreated: (s: SectionData) => void;
  onSectionUpdated: (s: SectionData) => void;
  onSectionDeleted: (sectionKey: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state for add/edit
  const [formKey, setFormKey] = useState('');
  const [formName, setFormName] = useState('');
  const [formNameEs, setFormNameEs] = useState('');
  const [formIcon, setFormIcon] = useState('Home');
  const [formOrder, setFormOrder] = useState(50);

  const resetForm = () => {
    setFormKey('');
    setFormName('');
    setFormNameEs('');
    setFormIcon('Home');
    setFormOrder(50);
    setAdding(false);
    setEditingKey(null);
    setError(null);
  };

  const startAdd = () => {
    resetForm();
    // Auto-suggest next display_order
    const maxOrder = sections.reduce((max, s) => Math.max(max, s.display_order), 0);
    setFormOrder(maxOrder + 10);
    setAdding(true);
    setEditingKey(null);
  };

  const startEdit = (s: SectionData) => {
    setFormKey(s.section_key);
    setFormName(s.section_name);
    setFormNameEs(s.section_name_es || '');
    setFormIcon(s.icon || 'Home');
    setFormOrder(s.display_order);
    setEditingKey(s.section_key);
    setAdding(false);
    setError(null);
  };

  const handleSave = async () => {
    setError(null);
    if (!formName.trim()) { setError('Section name is required'); return; }

    setSaving(true);
    try {
      if (adding) {
        // Generate section_key from name if not provided
        const key = formKey.trim() || formName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
        const res = await apiClient.post('/api/nav-sections', {
          section_key: key,
          section_name: formName.trim(),
          section_name_es: formNameEs.trim() || null,
          icon: formIcon,
          display_order: formOrder,
          is_default: false,
        });
        onSectionCreated(res.data);
      } else if (editingKey) {
        const res = await apiClient.put(`/api/nav-sections/${editingKey}`, {
          section_name: formName.trim(),
          section_name_es: formNameEs.trim() || null,
          icon: formIcon,
          display_order: formOrder,
        });
        onSectionUpdated(res.data);
      }
      resetForm();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to save section');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (sectionKey: string) => {
    const section = sections.find(s => s.section_key === sectionKey);
    if (!confirm(`Delete section "${section?.section_name}"? Pages in this section will become unassigned.`)) return;

    setSaving(true);
    setError(null);
    try {
      await apiClient.delete(`/api/nav-sections/${sectionKey}`);
      onSectionDeleted(sectionKey);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to delete section');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center gap-2 px-4 py-3 text-sm font-semibold text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700/50"
      >
        <Settings size={14} />
        Manage Sections
        <ChevronDown size={14} className={`ml-auto transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="border-t border-gray-200 px-4 pb-4 pt-3 dark:border-gray-700">
          {error && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300">
              {error}
            </div>
          )}

          {/* Existing sections list */}
          <div className="mb-3 space-y-1.5">
            {sections
              .sort((a, b) => a.display_order - b.display_order)
              .map((s) => (
                <div key={s.section_key} className="flex items-center gap-2 rounded-lg border border-gray-100 px-3 py-2 text-sm dark:border-gray-600">
                  <IconForName name={s.icon} size={14} />
                  <span className="flex-1 font-medium text-gray-800 dark:text-gray-100">{s.section_name}</span>
                  <span className="text-[10px] text-gray-400">{s.section_key}</span>
                  <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                    #{s.display_order}
                  </span>
                  {s.is_default && (
                    <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[9px] font-semibold text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                      default
                    </span>
                  )}
                  {editingKey !== s.section_key && (
                    <div className="flex items-center gap-0.5">
                      <button
                        onClick={() => startEdit(s)}
                        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                        title="Edit section"
                      >
                        <Pencil size={12} />
                      </button>
                      {!s.is_default && (
                        <button
                          onClick={() => handleDelete(s.section_key)}
                          disabled={saving}
                          className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950 dark:hover:text-red-400"
                          title="Delete section"
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
          </div>

          {/* Add/Edit form */}
          {(adding || editingKey) && (
            <div className="mb-3 space-y-2 rounded-lg border border-blue-200 bg-blue-50/50 p-3 dark:border-blue-800 dark:bg-blue-950/20">
              <div className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                {adding ? 'New Section' : `Edit: ${editingKey}`}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {adding && (
                  <div>
                    <label className="mb-0.5 block text-[10px] font-medium text-gray-500 dark:text-gray-400">Key (auto-generated if empty)</label>
                    <input
                      value={formKey}
                      onChange={(e) => setFormKey(e.target.value)}
                      placeholder="e.g. finance"
                      className="w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-xs dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                    />
                  </div>
                )}
                <div>
                  <label className="mb-0.5 block text-[10px] font-medium text-gray-500 dark:text-gray-400">Name *</label>
                  <input
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder="e.g. Finance"
                    className="w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-xs dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="mb-0.5 block text-[10px] font-medium text-gray-500 dark:text-gray-400">Name (Spanish)</label>
                  <input
                    value={formNameEs}
                    onChange={(e) => setFormNameEs(e.target.value)}
                    placeholder="e.g. Finanzas"
                    className="w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-xs dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="mb-0.5 block text-[10px] font-medium text-gray-500 dark:text-gray-400">Icon</label>
                  <select
                    value={formIcon}
                    onChange={(e) => setFormIcon(e.target.value)}
                    className="w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-xs dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  >
                    {AVAILABLE_ICONS.map((i) => (
                      <option key={i.name} value={i.name}>{i.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-0.5 block text-[10px] font-medium text-gray-500 dark:text-gray-400">Display Order</label>
                  <input
                    type="number"
                    value={formOrder}
                    onChange={(e) => setFormOrder(Number(e.target.value))}
                    className="w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-xs dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                  {adding ? 'Create Section' : 'Update Section'}
                </button>
                <button
                  onClick={resetForm}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Add button */}
          {!adding && !editingKey && (
            <button
              onClick={startAdd}
              className="flex items-center gap-1.5 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs font-medium text-gray-500 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-600 dark:border-gray-600 dark:text-gray-400 dark:hover:border-blue-500 dark:hover:bg-blue-950/30 dark:hover:text-blue-400"
            >
              <Plus size={12} />
              Add Section
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function NavigationLayoutAdmin({ organizationId, organizationName }: Props) {
  const tabbed = useTabbedChrome();
  const { t } = useAppTranslation();
  const [sections, setSections] = useState<SectionData[]>([]);
  const [items, setItems] = useState<NavItemData[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [orgRoles, setOrgRoles] = useState<OrgRole[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);

  // Live sidebar preview — the ACTUAL resolved sidebar for the selected role
  // (GET /nav-config, the same endpoint the app sidebar uses). Shows what renders
  // after access/feature/hub gating, so admins can trust the layout editor.
  type PreviewItem = { page_key: string; label: string; icon?: string | null };
  type PreviewSection = { section_key: string; section_name: string; icon?: string | null; items: PreviewItem[] };
  const [previewSections, setPreviewSections] = useState<PreviewSection[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(true);

  const selectedRole = useMemo(
    () => orgRoles.find(r => r.id === selectedRoleId) || null,
    [orgRoles, selectedRoleId]
  );

  // Group items by effective section
  const itemsBySection = useMemo(() => {
    const map: Record<string, NavItemData[]> = {};
    // Initialize all sections
    sections.forEach(s => { map[s.section_key] = []; });
    map['_unassigned'] = [];

    items.forEach(item => {
      const section = item.effective_section;
      if (section && map[section]) {
        map[section].push(item);
      } else {
        map['_unassigned'].push(item);
      }
    });

    // Sort items within each section: pinned first, then by effective_order
    Object.keys(map).forEach(key => {
      map[key].sort((a, b) => {
        if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
        return a.effective_order - b.effective_order;
      });
    });

    return map;
  }, [items, sections]);

  const activeItem = useMemo(() => {
    if (!activeId) return null;
    return items.find(i => i.page_key === activeId) || null;
  }, [activeId, items]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // -----------------------------------------------------------------------
  // DATA LOADING
  // -----------------------------------------------------------------------

  // Load the org-role list once per org. Virtual entries (id === -1) come from
  // /all-roles when a system role has no role_terminology row yet — we exclude
  // them from the nav-layout dropdown because nav_config rows must FK into a
  // real role_terminology row.
  useEffect(() => {
    if (!organizationId) return;
    apiClient
      .get(`/api/cbos/${organizationId}/all-roles`, {
        headers: { 'X-CBO-ID': organizationId.toString() },
      })
      .then(res => {
        const real: OrgRole[] = (res.data || []).filter((r: OrgRole) => r.id && r.id > 0);
        setOrgRoles(real);
        setSelectedRoleId(prev => {
          if (prev && real.some(r => r.id === prev)) return prev;
          // Default to the first non-admin role so admins typically land on
          // "Staff" / "Promotora" / etc. rather than super_admin.
          const nonAdmin = real.find(r => r.system_role !== 'super_admin' && r.system_role !== 'org_admin');
          return (nonAdmin || real[0])?.id ?? null;
        });
      })
      .catch(err => {
        setError(err.response?.data?.detail || err.message || 'Failed to load roles');
      });
  }, [organizationId]);

  const loadData = useCallback(async () => {
    if (!organizationId || !selectedRoleId) return;
    setLoading(true);
    setError(null);
    try {
      const [sectionsRes, itemsRes] = await Promise.all([
        apiClient.get('/api/nav-sections', {
          headers: { 'X-CBO-ID': organizationId.toString() }
        }),
        apiClient.get(
          `/api/organizations/${organizationId}/nav-config/items?org_role_id=${selectedRoleId}`,
          { headers: { 'X-CBO-ID': organizationId.toString() } }
        ),
      ]);
      setSections(sectionsRes.data);
      setItems(itemsRes.data);
      setDirty(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [organizationId, selectedRoleId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Fetch the real resolved sidebar for the selected role (mirrors the app).
  const loadPreview = useCallback(async () => {
    if (!organizationId || !selectedRoleId) { setPreviewSections([]); return; }
    setPreviewLoading(true);
    try {
      const res = await apiClient.get(
        `/api/organizations/${organizationId}/nav-config?org_role_id=${selectedRoleId}`,
        { headers: { 'X-CBO-ID': organizationId.toString() } }
      );
      setPreviewSections(res.data?.sections || []);
    } catch {
      setPreviewSections([]);
    } finally {
      setPreviewLoading(false);
    }
  }, [organizationId, selectedRoleId]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const handleRoleChange = (next: number) => {
    if (next === selectedRoleId) return;
    if (dirty && !confirm('You have unsaved changes for this role. Discard and switch?')) {
      return;
    }
    setSelectedRoleId(next);
  };

  // -----------------------------------------------------------------------
  // SAVE
  // -----------------------------------------------------------------------

  const handleSave = async () => {
    if (!selectedRoleId) return;
    setSaving(true);
    try {
      const updates = items.map((item) => ({
        page_key: item.page_key,
        nav_section_override: item.nav_section_override,
        display_order_override: item.display_order_override,
        nav_label_override: item.nav_label_override,
        nav_label_override_es: item.nav_label_override_es || null,
        pinned: item.pinned,
        hidden: item.hidden,
      }));

      await apiClient.post(
        `/api/organizations/${organizationId}/nav-config/bulk-update?org_role_id=${selectedRoleId}`,
        { updates },
        { headers: { 'X-CBO-ID': organizationId.toString() } }
      );

      // Dispatch event to refresh sidebar
      window.dispatchEvent(new CustomEvent('navConfigUpdated', {
        detail: { orgId: organizationId }
      }));

      setDirty(false);
      // Re-pull effective visibility (badges) + the live preview from the server.
      await loadData();
      loadPreview();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // -----------------------------------------------------------------------
  // RESET
  // -----------------------------------------------------------------------

  const handleReset = async () => {
    if (!selectedRoleId) return;
    const roleLabel = selectedRole?.display_name_en || `Role ${selectedRoleId}`;
    if (!confirm(`Reset all navigation customizations for ${roleLabel} to defaults?`)) return;
    setSaving(true);
    try {
      await apiClient.delete(
        `/api/organizations/${organizationId}/nav-config?org_role_id=${selectedRoleId}`,
        { headers: { 'X-CBO-ID': organizationId.toString() } }
      );
      await loadData();
      window.dispatchEvent(new CustomEvent('navConfigUpdated', {
        detail: { orgId: organizationId }
      }));
      setDirty(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to reset');
    } finally {
      setSaving(false);
    }
  };

  // -----------------------------------------------------------------------
  // PIN / LABEL ACTIONS
  // -----------------------------------------------------------------------

  // -----------------------------------------------------------------------
  // SECTION MANAGEMENT CALLBACKS
  // -----------------------------------------------------------------------

  const handleSectionCreated = (newSection: SectionData) => {
    setSections(prev => [...prev, newSection]);
  };

  const handleSectionUpdated = (updated: SectionData) => {
    setSections(prev => prev.map(s =>
      s.section_key === updated.section_key ? updated : s
    ));
  };

  const handleSectionDeleted = (sectionKey: string) => {
    setSections(prev => prev.filter(s => s.section_key !== sectionKey));
    // Move items from deleted section to unassigned
    setItems(prev => prev.map(item =>
      item.effective_section === sectionKey
        ? { ...item, effective_section: null, nav_section_override: null }
        : item
    ));
  };

  const handleTogglePin = (pageKey: string) => {
    setItems(prev => prev.map(item =>
      item.page_key === pageKey
        ? { ...item, pinned: !item.pinned }
        : item
    ));
    setDirty(true);
  };

  const handleToggleHidden = (pageKey: string) => {
    setItems(prev => prev.map(item =>
      item.page_key === pageKey
        ? { ...item, hidden: !item.hidden }
        : item
    ));
    setDirty(true);
  };

  const handleEditLabel = (pageKey: string, newLabel: string) => {
    setItems(prev => prev.map(item =>
      item.page_key === pageKey
        ? {
            ...item,
            nav_label_override: newLabel !== (item.nav_label || item.page_name) ? newLabel : null,
            effective_label: newLabel,
          }
        : item
    ));
    setDirty(true);
  };

  // -----------------------------------------------------------------------
  // DND HANDLERS
  // -----------------------------------------------------------------------

  function findSectionForItem(pageKey: string): string | null {
    for (const [sectionKey, sectionItems] of Object.entries(itemsBySection)) {
      if (sectionItems.some(i => i.page_key === pageKey)) return sectionKey;
    }
    return null;
  }

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const activeKey = active.id as string;
    const overKey = over.id as string;

    // Determine target section
    let targetSection: string | null = null;

    if (overKey.startsWith('section:')) {
      targetSection = overKey.replace('section:', '');
    } else {
      targetSection = findSectionForItem(overKey);
    }

    const sourceSection = findSectionForItem(activeKey);

    if (!targetSection || !sourceSection) return;

    if (targetSection === sourceSection) {
      // Reorder within same section
      const sectionItems = [...(itemsBySection[sourceSection] || [])];
      const oldIdx = sectionItems.findIndex(i => i.page_key === activeKey);
      const newIdx = overKey.startsWith('section:')
        ? sectionItems.length - 1
        : sectionItems.findIndex(i => i.page_key === overKey);

      if (oldIdx === -1 || newIdx === -1 || oldIdx === newIdx) return;

      const reordered = arrayMove(sectionItems, oldIdx, newIdx);

      // Update display_order_override based on new positions
      setItems(prev => {
        const updated = [...prev];
        reordered.forEach((item, idx) => {
          const globalIdx = updated.findIndex(i => i.page_key === item.page_key);
          if (globalIdx !== -1) {
            updated[globalIdx] = {
              ...updated[globalIdx],
              display_order_override: idx,
              effective_order: idx,
            };
          }
        });
        return updated;
      });
    } else {
      // Move to different section
      const effectiveTarget = targetSection === '_unassigned' ? null : targetSection;

      setItems(prev => prev.map(item => {
        if (item.page_key !== activeKey) return item;
        // Find position in target section
        const targetItems = itemsBySection[targetSection!] || [];
        const overIdx = overKey.startsWith('section:')
          ? targetItems.length
          : targetItems.findIndex(i => i.page_key === overKey);
        const newOrder = overIdx >= 0 ? overIdx : targetItems.length;

        return {
          ...item,
          nav_section_override: effectiveTarget,
          effective_section: effectiveTarget,
          display_order_override: newOrder,
          effective_order: newOrder,
        };
      }));
    }

    setDirty(true);
  };

  // -----------------------------------------------------------------------
  // RENDER
  // -----------------------------------------------------------------------

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-gray-400" />
        <span className="ml-2 text-sm text-gray-500">Loading navigation layout…</span>
      </div>
    );
  }

  if (error && !items.length) {
    return (
      <div className="mx-auto max-w-lg rounded-xl border border-red-200 bg-red-50 p-6 text-center dark:border-red-800 dark:bg-red-950/30">
        <AlertCircle size={24} className="mx-auto mb-2 text-red-500" />
        <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        <button
          onClick={loadData}
          className="mt-3 rounded-lg bg-red-100 px-4 py-1.5 text-xs font-medium text-red-700 hover:bg-red-200 dark:bg-red-900 dark:text-red-300 dark:hover:bg-red-800"
        >
          Retry
        </button>
      </div>
    );
  }

  const customCount = items.filter(i =>
    i.nav_section_override != null || i.display_order_override != null || i.nav_label_override != null
  ).length;

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          {!tabbed && (
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Navigation Layout
          </h2>
          )}
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Drag pages between sections to customize the sidebar for {organizationName || 'this organization'}. Each role has its own layout.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
              Unsaved changes
            </span>
          )}
          <button
            onClick={handleReset}
            disabled={saving}
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            <RotateCcw size={12} /> Reset role
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !dirty}
            className={`flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-xs font-semibold text-white ${
              dirty
                ? 'bg-v2accent hover:bg-v2accent/90'
                : 'cursor-not-allowed bg-gray-300 dark:bg-gray-600'
            }`}
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Save layout
          </button>
        </div>
      </div>

      {/* Prominent role context — the layout below is for THIS role */}
      <div className="flex flex-col gap-2 rounded-xl border-2 border-indigo-300 bg-indigo-50 px-4 py-3 dark:border-indigo-700 dark:bg-indigo-950/40 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-sm text-indigo-900 dark:text-indigo-200">
          <UserCog size={18} className="shrink-0 text-indigo-600 dark:text-indigo-400" />
          <span>
            Editing the sidebar for role:{' '}
            <strong className="text-base font-bold">
              {selectedRole?.display_name_en || (orgRoles.length === 0 ? 'Loading…' : 'Select a role')}
            </strong>
            {selectedRole?.system_role && selectedRole.system_role !== selectedRole.role_type && (
              <span className="ml-1 text-xs font-normal text-indigo-700/70 dark:text-indigo-300/70">
                (system role: {selectedRole.system_role})
              </span>
            )}
          </span>
        </div>
        <label className="flex items-center gap-2 text-xs font-semibold text-indigo-900 dark:text-indigo-200">
          Configure role:
          <select
            value={selectedRoleId ?? ''}
            onChange={(e) => handleRoleChange(Number(e.target.value))}
            disabled={saving || orgRoles.length === 0}
            className="rounded-lg border-2 border-indigo-300 bg-white px-3 py-1.5 text-sm font-semibold text-indigo-900 focus:border-indigo-500 focus:outline-none dark:border-indigo-600 dark:bg-gray-900 dark:text-indigo-100"
          >
            {orgRoles.length === 0 && <option value="">Loading roles…</option>}
            {orgRoles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.display_name_en}
                {r.system_role && r.system_role !== r.role_type ? ` (${r.system_role})` : ''}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Stats bar */}
      <div className="flex gap-4 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-xs dark:border-gray-700 dark:bg-gray-800">
        <span className="text-gray-500 dark:text-gray-400">
          <strong className="text-gray-800 dark:text-gray-100">{sections.length}</strong> sections
        </span>
        <span className="text-gray-500 dark:text-gray-400">
          <strong className="text-gray-800 dark:text-gray-100">{items.filter(i => i.effective_section).length}</strong> sidebar items
        </span>
        <span className="text-gray-500 dark:text-gray-400">
          <strong className="text-gray-800 dark:text-gray-100">{customCount}</strong> customized
        </span>
        <span className="text-gray-500 dark:text-gray-400">
          <strong className="text-gray-800 dark:text-gray-100">{(itemsBySection['_unassigned'] || []).length}</strong> unassigned
        </span>
      </div>

      {/* Live sidebar preview — the ACTUAL resolved sidebar for this role,
          after access / feature / hub gating (same source as the app sidebar). */}
      <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <button
          onClick={() => setPreviewOpen((o) => !o)}
          className="flex w-full items-center justify-between px-4 py-3 text-left"
        >
          <span className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
            <Eye size={16} className="text-indigo-600 dark:text-indigo-400" />
            Sidebar preview
            <span className="font-normal text-gray-400">
              — what {selectedRole?.display_name_en || 'this role'} actually sees
            </span>
            {previewLoading && <Loader2 size={13} className="animate-spin text-gray-400" />}
          </span>
          <ChevronDown size={16} className={`text-gray-400 transition-transform ${previewOpen ? 'rotate-180' : ''}`} />
        </button>
        {previewOpen && (
          <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-700/60">
            {previewSections.length === 0 ? (
              <p className="text-xs text-gray-400">
                {previewLoading ? 'Loading…' : 'No sections render in the sidebar for this role.'}
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-4">
                {previewSections.map((sec) => (
                  <div key={sec.section_key} className="min-w-0">
                    <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                      <IconForName name={sec.icon ?? null} size={12} />
                      <span className="truncate">{sec.section_name}</span>
                    </div>
                    <ul className="space-y-1">
                      {sec.items.map((it) => (
                        <li key={it.page_key} className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-200">
                          <IconForName name={it.icon ?? null} size={12} />
                          <span className="truncate">{it.label}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
            <p className="mt-3 text-[11px] text-gray-400">
              Reflects live role-access, feature, and hub gating. A page badged in the layout below
              but missing here won&apos;t show for this role.
            </p>
          </div>
        )}
      </div>

      {/* Section Manager */}
      <SectionTerminologyPanel organizationId={organizationId} />

      <SectionManager
        sections={sections}
        onSectionCreated={handleSectionCreated}
        onSectionUpdated={handleSectionUpdated}
        onSectionDeleted={handleSectionDeleted}
      />

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300">
          {error}
        </div>
      )}

      {/* DnD Layout */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
          {sections
            .filter(s => s.is_active)
            .sort((a, b) => a.display_order - b.display_order)
            .map((section) => (
              <SectionColumn
                key={section.section_key}
                section={section}
                items={itemsBySection[section.section_key] || []}
                onTogglePin={handleTogglePin}
                onEditLabel={handleEditLabel}
                onToggleHidden={handleToggleHidden}
              />
            ))}
        </div>

        {/* Unassigned pool */}
        <UnassignedPool
          items={itemsBySection['_unassigned'] || []}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />

        <DragOverlay>
          {activeItem && <DragOverlayItem item={activeItem} />}
        </DragOverlay>
      </DndContext>

      {/* Help text */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
        <strong>Tips:</strong> Drag pages between sections to reorganize the sidebar.
        Click the pencil icon to rename items. Pin items to keep them at the top.
        Click the trash icon to remove a page from this role's sidebar (reversible — click the restore icon to bring it back, or Reset role to clear everything).
        Pages that won't render for this role are grayed out with a reason badge — <em>disabled</em> (turned off in Page Access), <em>no access</em> (role permission), <em>feature off</em> (capability not enabled), or <em>hub only</em>. Use the Sidebar preview above to see exactly what renders.
        Changes take effect immediately after saving for all users with the v2 sidebar.
      </div>
    </div>
  );
}

export default NavigationLayoutAdmin;
